import { useApolloClient } from '@vue/apollo-composable';
import { computed, onScopeDispose, watch } from 'vue';

import { useAuthStore } from '@/entities/auth';
import { clearPushReturnIntent } from '@/features/push-navigation';
import { registerPushNotificationLocalStateListeners, usePushNotifications } from '@/features/push-notifications';
import { registerLogoutLifecycleHook } from '@/features/session';
import { useLocationStorage, useOrganizationStorage } from '@/shared/config';

export interface UsePwaRuntimeOptions {
  readonly pushEnabled?: boolean;
}

export const usePwaRuntime = ({ pushEnabled = true }: UsePwaRuntimeOptions = {}): void => {
  const authStore = useAuthStore();
  const pushNotifications = pushEnabled ? usePushNotifications() : null;
  const { clearLocationId } = useLocationStorage();
  const { resetOrganizationId } = useOrganizationStorage();
  const { resolveClient } = useApolloClient();

  const clearSessionLocalState = async (): Promise<void> => {
    try {
      clearLocationId();
      resetOrganizationId();
      clearPushReturnIntent();
    } catch {
      console.warn('[session.cleanup] Не удалось очистить локальное состояние', {
        kind: 'storage-cleanup-failure',
        phase: 'session-transition',
      });
    }

    try {
      await resolveClient().clearStore();
    } catch {
      console.warn('[session.cleanup] Не удалось очистить Apollo cache', {
        kind: 'cache-cleanup-failure',
        phase: 'session-transition',
      });
    }
  };

  const unregisterLogoutHook = registerLogoutLifecycleHook({
    beforeLogoutRequest: async () => {
      await pushNotifications?.cleanupForLogout();
    },
    afterLogoutRequest: clearSessionLocalState,
  });

  let sessionTransitionPromise: Promise<void> = Promise.resolve();
  const stopUserWatch = watch(
    () => authStore.user?.id ?? null,
    (userId, previousUserId) => {
      if (previousUserId && previousUserId !== userId) {
        pushNotifications?.setUser(null);
        sessionTransitionPromise = (async () => {
          await pushNotifications?.cleanupForSessionLoss();
          await clearSessionLocalState();
        })().catch(() => {
          console.warn('[session.cleanup] Не удалось завершить локальный cleanup', {
            kind: 'lifecycle-cleanup-failure',
            phase: 'session-transition',
          });
        });
      }
    },
    { immediate: true },
  );

  const stablePushUserId = computed(() => {
    if (!pushNotifications || !authStore.isContextChecked || !authStore.user) {
      return null;
    }

    return authStore.user.id;
  });
  const stopStableContextWatch = watch(
    stablePushUserId,
    async (userId) => {
      if (!userId) {
        return;
      }

      await sessionTransitionPromise;
      if (stablePushUserId.value !== userId) {
        return;
      }

      pushNotifications?.setUser(userId);
      await pushNotifications?.reconcile();
    },
    { immediate: true },
  );
  const unregisterLocalStateListeners = pushNotifications
    ? registerPushNotificationLocalStateListeners()
    : () => undefined;

  onScopeDispose(() => {
    stopUserWatch();
    stopStableContextWatch();
    unregisterLocalStateListeners();
    unregisterLogoutHook();
  });
};
